<template>
  <!-- 홈 하단 컨트롤 패널: 돌보기(밥/놀이) + 시간대 슬라이더 + 이동(병원/상가). -->
  <div class="home-control-panel">
    <!-- 아이템 피커 (밥주기/놀아주기 버튼 위에 표시) -->
    <transition name="pick-pop">
      <div v-if="picker" class="pick-pop">
        <div v-if="loadingPick" class="pick-pop__empty">불러오는 중…</div>
        <template v-else-if="items.length">
          <button
            v-for="it in items"
            :key="it.id"
            class="pick-chip"
            :disabled="busy"
            @click="onPick(it)"
          >
            <img v-if="it.imageUrl" :src="it.imageUrl" class="pick-chip__img" />
            <span v-else class="pick-chip__emoji">{{ it.emoji }}</span>
            <span class="pick-chip__name">{{ it.name }}</span>
            <span class="pick-chip__meta">
              {{ picker === "toy" ? `🛠️ ${it.usesLeft ?? it.durability}` : `×${it.qty}` }}
            </span>
          </button>
        </template>
        <div v-else class="pick-pop__empty">
          {{ picker === "toy" ? "보유한 장난감이 없어요." : "보유한 음식이 없어요." }}
          <a class="pick-pop__link" @click="goStore">펫스토어 가기</a>
        </div>
      </div>
    </transition>

    <!-- 액션 버튼 줄 (위): 좌=돌보기 / 우=이동(병원·상가) -->
    <div class="action-row">
      <div class="action-group">
        <button class="ctrl-btn" :class="{ active: picker === 'food' }" title="밥주기" @click="togglePicker('food')">
          🍖
        </button>
        <button class="ctrl-btn" :class="{ active: picker === 'toy' }" title="놀아주기" @click="togglePicker('toy')">
          🎾
        </button>
        <button class="ctrl-btn" title="씻기기 (준비중)" disabled>🫧</button>
        <button class="ctrl-btn" title="잠재우기 (준비중)" disabled>💤</button>
      </div>
      <div class="action-group">
        <button class="ctrl-btn" title="병원" @click="goHospital">🏥</button>
        <button class="ctrl-btn" title="상가(펫스토어)" @click="goStore">🏪</button>
      </div>
    </div>

    <!-- 시간대 슬라이더 (아래) -->
    <div class="time-slider">
      <span class="time-slider__icon">{{ hour >= 6 && hour < 18 ? "☀️" : "🌙" }}</span>
      <v-slider
        v-model="hour"
        :min="0"
        :max="24"
        :step="0.5"
        hide-details
        dense
        class="time-slider__bar"
        color="white"
        track-color="rgba(255,255,255,0.4)"
      ></v-slider>
      <span class="time-slider__label">{{ hourLabel }}</span>
    </div>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";

export default {
  name: "HomeControlPanel",
  props: {
    // 시간대(0~24). v-model로 부모(HomePage)와 동기화.
    value: { type: Number, default: 12 },
    // 내 버츄펫 도메인 엔티티(MyPet) — 밥주기/놀아주기 행동의 단일 진입점.
    pet: { type: Object, default: null },
  },
  data() {
    return {
      picker: null, // null | 'food' | 'toy'
      loadingPick: false,
      items: [], // 현재 피커의 보유 아이템(type 필터, qty>0)
      busy: false,
    };
  },
  computed: {
    hour: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    hourLabel() {
      const h = Math.floor(this.hour) % 24;
      const m = Math.round((this.hour - Math.floor(this.hour)) * 60);
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    },
  },
  methods: {
    async togglePicker(kind) {
      this.picker = this.picker === kind ? null : kind;
      if (this.picker) await this.loadItems();
    },
    async loadItems() {
      this.loadingPick = true;
      try {
        const type = this.picker === "toy" ? "TOY" : "FOOD";
        const inv = await SupabaseApi.Item.fetchInventory();
        this.items = inv.filter((i) => i.type === type && i.qty > 0);
      } catch (e) {
        console.error("loadItems failed", e);
        this.items = [];
      } finally {
        this.loadingPick = false;
      }
    },
    async onPick(it) {
      if (this.busy || !this.pet) return;
      this.busy = true;
      try {
        if (this.picker === "food") {
          const result = await this.pet.feed(it, 1);
          it.qty = result.qtyLeft;
          if (it.qty <= 0) this.items = this.items.filter((x) => x.id !== it.id);
          this.$emit("fed", { item: it, result });
        } else {
          const result = await this.pet.play(it);
          if (result.broke) {
            it.qty -= 1;
            it.usesLeft = it.durability; // 다음 개체는 내구도 가득(표시용)
            if (it.qty <= 0) this.items = this.items.filter((x) => x.id !== it.id);
          } else {
            it.usesLeft = result.usesLeft;
          }
          this.$emit("played", { item: it, result });
        }
      } catch (e) {
        console.error("care action failed", e);
        if (this.$notify) this.$notify({ type: "warning", title: "실패했어요", duration: 2000 });
      } finally {
        this.busy = false;
      }
    },
    goStore() {
      this.$router.push("/pet/store");
    },
    goHospital() {
      this.$router.push("/pet/hospital");
    },
  },
};
</script>

<style scoped>
.home-control-panel {
  /* 구 absolute 하단 고정 → HomePage .home-stage-group(flex 컬럼) flow 안에서 배치 */
  position: relative;
  z-index: 2;
  width: min(560px, 76vw);
}
.action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
}
.action-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ctrl-btn {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  font-size: 22px;
  background: rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(6px);
  transition: transform 0.12s, background 0.12s;
}
.ctrl-btn:hover {
  transform: translateY(-2px);
}
.ctrl-btn.active {
  background: rgba(142, 111, 214, 0.85);
}
.ctrl-btn:disabled {
  opacity: 0.4;
  cursor: default;
  transform: none;
}

.time-slider {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 18px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(6px);
}
.time-slider__bar {
  flex: 1 1 auto;
}
.time-slider__icon {
  font-size: 18px;
}
.time-slider__label {
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  min-width: 44px;
  text-align: right;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

/* 아이템 피커 */
.pick-pop {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(8px);
  max-height: 220px;
  overflow-y: auto;
}
.pick-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 72px;
  padding: 8px 4px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  transition: transform 0.12s, background 0.12s;
}
.pick-chip:hover:not(:disabled) {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.22);
}
.pick-chip:disabled {
  opacity: 0.5;
}
.pick-chip__img {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
}
.pick-chip__emoji {
  font-size: 30px;
  line-height: 36px;
}
.pick-chip__name {
  font-size: 11px;
  max-width: 64px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pick-chip__meta {
  font-size: 11px;
  font-weight: 700;
  color: #ffd9a8;
}
.pick-pop__empty {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  padding: 8px 4px;
}
.pick-pop__link {
  color: #ffd9a8;
  font-weight: 700;
  margin-left: 6px;
  cursor: pointer;
  text-decoration: underline;
}
.pick-pop-enter-active,
.pick-pop-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.pick-pop-enter,
.pick-pop-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
